import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, openWithAuth } from './api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Users, Home, Handshake, GraduationCap, ShieldCheck, DollarSign,
  Plus, Loader2, Building2, ExternalLink,
  FileSignature, ChevronRight, Ban, Trash2, UserPlus,
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useTabParam } from './shared/useTabParam';
import { confirmDialog, promptDialog } from '@/components/dialogs/DialogHost';
import { errorMessage } from './errors';
import ContactSearchInput, { FIELD_CLASS } from './shared/ContactSearchInput';
import ContactPicker from './shared/ContactPicker';
import LoanLinkControl from './re/LoanLinkControl';
import HubContactsTab from './re/HubContactsTab';
import HubAgentsTab from './re/HubAgentsTab';
import HubCommissionsTab from './re/HubCommissionsTab';
import HubComplianceTab from './re/HubComplianceTab';
import { fmt$, dealLabel } from './re/format';
import { AutoDraftModal } from './re/ContractDraft';
import TransactionTeam from './re/TransactionTeam';
import TransactionShareLink from './re/TransactionShareLink';
import AlertsStrip from './board/AlertsStrip';
import { reApi } from './workspacesApi';
import { TX_ALERT_GROUPS, TX_ALERT_DAYS } from './re/alertGroups';
import { contactFields } from './shared/useTextRecipient';
import MoneyInput from './shared/MoneyInput';
import NumberInput from './shared/NumberInput';
import { formatDate, fromApi } from './kit/time';


const STAGE_LABEL = {
  prospect: 'Prospect', showing: 'Showing', offer_out: 'Offer Out', offer_accepted: 'Offer Accepted',
  under_contract: 'Under Contract', inspection: 'Inspection', appraisal: 'Appraisal',
  financing: 'Financing', clear_to_close: 'Clear to Close', closed: 'Closed',
  cancelled: 'Cancelled', fell_through: 'Fell Through',
};
const STAGE_ORDER = Object.keys(STAGE_LABEL);

const HUB_TABS = ['transactions', 'contacts', 'contracts', 'agents', 'commissions', 'compliance'];

export default function RealEstateHubPage() {
  const [tab, setTab] = useTabParam('transactions', { valid: HUB_TABS });
  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-bold flex items-center gap-2" style={{ color: '#070B5F' }}>
          <Building2 /> Real Estate Brokerage
        </h1>
        <p className="text-slate-500 mt-1">Contacts, transactions, agents, onboarding, and TREC compliance — all in one place.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 md:grid-cols-6 h-auto mb-4">
          <TabsTrigger value="transactions" data-testid="re-tab-transactions"><Handshake size={13} className="mr-1.5" />Transactions</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="re-tab-contacts"><Users size={13} className="mr-1.5" />Contacts</TabsTrigger>
          <TabsTrigger value="contracts" data-testid="re-tab-contracts"><FileSignature size={13} className="mr-1.5" />TREC Contracts</TabsTrigger>
          <TabsTrigger value="agents" data-testid="re-tab-agents"><GraduationCap size={13} className="mr-1.5" />Agents</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="re-tab-commissions"><DollarSign size={13} className="mr-1.5" />Commissions</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="re-tab-compliance"><ShieldCheck size={13} className="mr-1.5" />TREC Compliance</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions"><TransactionsTab /></TabsContent>
        <TabsContent value="contacts"><HubContactsTab /></TabsContent>
        <TabsContent value="contracts"><ContractsTab /></TabsContent>
        <TabsContent value="agents"><HubAgentsTab /></TabsContent>
        <TabsContent value="commissions"><HubCommissionsTab /></TabsContent>
        <TabsContent value="compliance"><HubComplianceTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// =========================================================================
// TRANSACTIONS
// =========================================================================
function TransactionsTab() {
  const [txs, setTxs] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [alerts, setAlerts] = useState(null);
  const navigate = useNavigate();
  const load = () => {
    // The deadline strip is a nicety: it never blocks the list.
    reApi.alerts(TX_ALERT_DAYS).then(setAlerts).catch(() => setAlerts(null));
    return api.get('/re/transactions').then(r => setTxs(r.data));
  };
  useEffect(() => { load(); }, []);
  const alertLabel = (i) => { const t = txs.find(x => x.transaction_id === i.transaction_id); return t?.buyer_names || t?.seller_names || null; };

  const setStage = async (id, stage) => {
    // Closing posts the agent's commission to the ledger, so one stray pick must not do it.
    if (stage === 'closed' && !(await confirmDialog({ title: 'Move to Closed?', description: 'Closing posts this deal\'s commission to the ledger.', confirmText: 'Close deal' }))) return;
    try {
      await api.patch(`/re/transactions/${id}`, { stage });
      load();
      toast({ title: `Moved to ${STAGE_LABEL[stage]}` });
    } catch (e) {
      toast({ title: 'Could not move the transaction', description: errorMessage(e) });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{txs.length} transactions</div>
        <button onClick={() => setShowNew(true)} data-testid="new-transaction-btn" className="text-xs font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: '#6BA82E' }}><Plus size={12} /> New Transaction</button>
      </div>

      <AlertsStrip alerts={alerts} days={TX_ALERT_DAYS} product="real_estate" groups={TX_ALERT_GROUPS} noun="deadline" testIdPrefix="re-hub"
        labelFor={alertLabel} onOpen={(i) => navigate(`/portal/real-estate/transactions/${i.transaction_id}`)} />

      <div className="grid gap-3">
        {txs.map(t => <TransactionCard key={t.transaction_id} tx={t} onStage={setStage} onChange={load} />)}
        {txs.length === 0 && <EmptyBox msg="No transactions yet. Create one to start tracking a deal." />}
      </div>

      {showNew && <NewTransactionModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function TransactionCard({ tx, onStage, onChange }) {
  const [showShare, setShowShare] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const base = `/re/transactions/${tx.transaction_id}`;
  const del = async () => {
    if (!(await confirmDialog({ title: 'Delete this transaction?', description: 'This also turns off its status link. It cannot be undone.', confirmText: 'Delete', destructive: true }))) return;
    try {
      await api.delete(base);
      toast({ title: 'Transaction deleted' });
      onChange();
    } catch (e) { toast({ title: 'Delete failed', description: errorMessage(e) }); }
  };
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={`transaction-card-${tx.transaction_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded" style={{ background: '#F3FAE8', color: '#4c7f21' }}>{tx.side} side</span>
            <span className="font-mono text-xs text-slate-400">{tx.transaction_id}</span>
            <LoanLinkControl tx={tx} onChange={onChange} />
          </div>
          {(tx.buyer_names || tx.seller_names || tx.property_address?.street) && (
            <div className="mt-2 text-sm font-semibold text-slate-700" data-testid={`tx-who-${tx.transaction_id}`}>
              {[tx.buyer_names || tx.seller_names, tx.property_address?.street].filter(Boolean).join(' · ')}
            </div>
          )}
          <div className="mt-1 font-serif text-lg font-bold" style={{ color: '#070B5F' }}>
            <Link to={`/portal/real-estate/transactions/${tx.transaction_id}`} className="hover:underline" data-testid={`open-tx-${tx.transaction_id}`}>{fmt$(tx.contract_price)}</Link>
            {tx.close_date && <span className="text-sm font-normal text-slate-500 ml-3">Close: {formatDate(tx.close_date)}</span>}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {tx.lender_name && `Lender: ${tx.lender_name} · `}
            {tx.title_company && `Title: ${tx.title_company}`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={tx.stage} onValueChange={(v) => onStage(tx.transaction_id, v)}>
            <SelectTrigger className="w-44" data-testid={`stage-select-${tx.transaction_id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{STAGE_ORDER.map(s => <SelectItem key={s} value={s}>{STAGE_LABEL[s]}</SelectItem>)}</SelectContent>
          </Select>
          <Link to={`/portal/real-estate/transactions/${tx.transaction_id}`} className="text-xs font-semibold px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1" style={{ color: '#070B5F' }} data-testid={`open-tx-btn-${tx.transaction_id}`}><ChevronRight size={11} /> Open</Link>
          <button onClick={() => setShowShare((v) => !v)} className="text-xs px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`share-tx-link-${tx.transaction_id}`}><ExternalLink size={11} /> Share</button>
          <button onClick={() => setShowTeam((v) => !v)} aria-expanded={showTeam} className="text-xs px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`team-tx-${tx.transaction_id}`}><UserPlus size={11} /> Team</button>
          <button onClick={del} aria-label="Delete transaction" title="Delete transaction" className="text-xs px-2 py-1.5 rounded border border-slate-200 text-red-600 hover:bg-red-50 inline-flex items-center" data-testid={`delete-tx-${tx.transaction_id}`}><Trash2 size={12} /></button>
        </div>
      </div>
      {showTeam && <div className="mt-4 pt-4 border-t border-slate-100"><TransactionTeam transactionId={tx.transaction_id} /></div>}
      {showShare && <div className="mt-4 pt-4 border-t border-slate-100"><TransactionShareLink tx={tx} onChange={onChange} /></div>}
    </div>
  );
}

function NewTransactionModal({ onClose, onCreated }) {
  const [f, setF] = useState({ side: 'buyer', stage: 'prospect', contract_price: 0, commission_pct: 3.0, contact_id: '' });
  const [contact, setContact] = useState(null); // the picked contacts_v2 record
  const [error, setError] = useState('');
  // A transaction is anchored to a contact (ticket 98 N8); the address can come later.
  // The picker searches the tenant's unified contacts as you type (ticket 206).
  const pick = (id, c) => { setF(x => ({ ...x, contact_id: id || '' })); setContact(id ? c || null : null); setError(''); };
  const submit = async () => {
    if (!f.contact_id || !contact) { setError('Pick the contact this transaction is for'); return; }
    setError('');
    const who = contactFields(contact);
    who.name = who.name || [contact.first_name, contact.last_name].filter(Boolean).join(' ');
    const names = f.side === 'listing' ? { seller_names: who.name } : { buyer_names: who.name, buyer_email: who.email || undefined, buyer_phone: who.phone || undefined };
    try {
      await api.post('/re/transactions', { ...f, ...names });
      onCreated();
    } catch (e) {
      toast({ title: 'Could not create transaction', description: errorMessage(e) });
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="font-serif text-lg font-bold mb-4" style={{ color: '#070B5F' }}>New Transaction</div>
        <div className="space-y-3">
          <div>
            <ContactPicker value={f.contact_id || null} onChange={pick} label="Contact" allowNone={false} testId="new-tx-contact" />
            {error && <div className="text-xs text-red-600 mt-1" data-testid="new-tx-contact-error">{error}</div>}
          </div>
          <div><Label className="text-xs">Side</Label>
            <Select value={f.side} onValueChange={(v) => setF(x => ({ ...x, side: v }))}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{[['buyer','Buyer'],['listing','Listing'],['dual','Dual']].map(([k,l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Contract Price</Label><MoneyInput value={f.contract_price} onChange={(n) => setF(x => ({ ...x, contract_price: n ?? 0 }))} className="mt-1" data-testid="new-tx-price" /></div>
          <div><Label className="text-xs">Commission %</Label><NumberInput value={f.commission_pct} onChange={(n) => setF(x => ({ ...x, commission_pct: n ?? 0 }))} suffix="%" className="mt-1" /></div>
          <div><Label className="text-xs">Lender</Label><Input value={f.lender_name || ''} onChange={(e) => setF(x => ({ ...x, lender_name: e.target.value }))} placeholder="Top Choice Lending / other" className="mt-1" /></div>
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
          <button onClick={submit} className="text-sm px-4 py-2 rounded-lg text-white" style={{ background: '#6BA82E' }} data-testid="submit-new-tx">Create</button>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// CONTRACTS — TREC library + built-in e-sign
// =========================================================================
function ContractsTab() {
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showDraft, setShowDraft] = useState(false);
  // `?contract=<id>` (from the deal page's contract rows) opens that contract.
  const [params] = useSearchParams();
  const [openId, setOpenId] = useState(params.get('contract'));

  const load = () => api.get('/re/contracts').then(r => setContracts(r.data));
  useEffect(() => {
    load();
    api.get('/re/contract-templates').then(r => setTemplates(r.data));
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="text-sm text-slate-500">{contracts.length} contracts on file · {templates.length} TREC templates available</div>
        <div className="flex gap-2">
          <button onClick={() => setShowDraft(true)} data-testid="auto-draft-btn" className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5" style={{ color: '#070B5F' }}><FileSignature size={12} /> Draft from a deal</button>
          <button onClick={() => setShowNew(true)} data-testid="new-contract-btn" className="text-xs font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: '#6BA82E' }}><Plus size={12} /> New Contract</button>
        </div>
      </div>

      <div className="grid gap-2">
        {contracts.map(c => (
          <div key={c.contract_id} className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`contract-${c.contract_id}`}>
            <button onClick={() => setOpenId(openId === c.contract_id ? null : c.contract_id)} className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 text-left">
              <FileSignature size={18} style={{ color: '#6BA82E' }} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold" style={{ color: '#070B5F' }}>{c.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {c.signatories.length} signatories · {c.signatories.filter(s => s.signed_at).length} signed · Status: <b>{c.status.replace('_', ' ')}</b>
                </div>
              </div>
              <ChevronRight size={16} className={`text-slate-400 transition ${openId === c.contract_id ? 'rotate-90' : ''}`} />
            </button>
            {openId === c.contract_id && <ContractDetail contract={c} onUpdate={load} />}
          </div>
        ))}
        {contracts.length === 0 && <EmptyBox msg="No contracts yet. Start a new TREC contract to auto-populate fields from a transaction." />}
      </div>

      {showDraft && <AutoDraftModal templates={templates} onClose={() => setShowDraft(false)} onDrafted={(c) => { setShowDraft(false); setOpenId(c.contract_id); load(); toast({ title: 'Contract drafted', description: 'Check the fields and add signers.' }); }} />}
      {showNew && <NewContractModal templates={templates} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function ContractDetail({ contract, onUpdate }) {
  const [c, setC] = useState(contract);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(null);
  const [consent, setConsent] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [previewing, setPreviewing] = useState(false);

  const uField = (key, v) => setDirty(d => ({ ...d, [key]: v }));
  const getVal = (f) => dirty[f.key] !== undefined ? dirty[f.key] : (f.value ?? '');

  const save = async () => {
    if (Object.keys(dirty).length === 0) return;
    setSaving(true);
    try {
      const res = await api.patch(`/re/contracts/${c.contract_id}/fields`, { fields: dirty });
      setC(res.data); setDirty({}); toast({ title: 'Fields saved' }); onUpdate();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || 'Error' }); }
    finally { setSaving(false); }
  };

  const sign = async (signatoryId) => {
    if (!consent) { toast({ title: 'Consent to e-sign required' }); return; }
    if (!typedName) { toast({ title: 'Please type your full legal name' }); return; }
    try {
      const res = await api.post(`/re/contracts/${c.contract_id}/sign`, {
        signatory_id: signatoryId, typed_name: typedName, consent_to_esign: true,
      });
      setC(res.data); toast({ title: 'Signed', description: `Status: ${res.data.status}` });
      setSigning(null); setConsent(false); setTypedName(''); onUpdate();
    } catch (e) { toast({ title: 'Sign failed', description: e.response?.data?.detail || 'Error' }); }
  };

  // Ticket 206: void keeps the contract and its audit trail, locks it, and notifies nobody.
  const voidContract = async () => {
    const reason = await promptDialog({
      title: 'Void this contract?',
      description: 'It stays on file marked voided and can no longer be edited or signed. Nobody is notified.',
      confirmText: 'Void contract', placeholder: 'Reason (optional)', allowEmpty: true,
    });
    if (reason === null || reason === undefined) return;
    try {
      await api.post(`/re/contracts/${c.contract_id}/void`, null, { params: { reason: reason.trim() } });
      setC((x) => ({ ...x, status: 'voided' })); toast({ title: 'Contract voided' }); onUpdate();
    } catch (e) { toast({ title: 'Void failed', description: errorMessage(e) }); }
  };

  const addSignatory = async (sig) => {
    try {
      const res = await api.post(`/re/contracts/${c.contract_id}/signatories`, sig);
      setC(res.data); toast({ title: 'Signatory added' }); onUpdate();
      return true;
    } catch (e) { toast({ title: 'Could not add signatory', description: errorMessage(e) }); return false; }
  };

  const openPreview = async () => {
    setPreviewing(true);
    try { await openWithAuth(`/re/contracts/${c.contract_id}/preview.html`, { allowHtml: true }); }
    finally { setPreviewing(false); }
  };

  // Group fields by section
  const grouped = [];
  let current = null;
  c.fields.forEach(f => {
    if (f.section && f.field_type === 'text' && !f.value && f.key.endsWith('_section')) {
      // A section header field (has label = section, no value expected)
      current = { section: f.label, items: [] };
      grouped.push(current);
    } else if (f.section && !current) {
      current = { section: f.section, items: [f] };
      grouped.push(current);
    } else {
      if (!current) { current = { section: 'Fields', items: [] }; grouped.push(current); }
      current.items.push(f);
    }
  });

  const isLocked = c.status === 'fully_signed' || c.status === 'voided';

  return (
    <div className="border-t border-slate-100 p-4 space-y-4">
      {/* Save + Preview toolbar */}
      <div className="flex gap-2 justify-end flex-wrap">
        <button onClick={openPreview} disabled={previewing} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-40" data-testid={`preview-${c.contract_id}`}>
          {previewing ? <Loader2 size={12} className="animate-spin" /> : <ExternalLink size={12} />} Preview / Print
        </button>
        {!isLocked && (
          <button onClick={voidContract} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5" data-testid={`void-${c.contract_id}`}>
            <Ban size={12} /> Void
          </button>
        )}
        {!isLocked && (
          <button onClick={save} disabled={saving || Object.keys(dirty).length === 0} className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: '#6BA82E' }} data-testid={`save-fields-${c.contract_id}`}>
            {saving ? <Loader2 size={12} className="animate-spin" /> : null} Save Fields {Object.keys(dirty).length > 0 && `(${Object.keys(dirty).length})`}
          </button>
        )}
      </div>

      {/* Fields grouped by section */}
      <div className="space-y-3">
        {grouped.map((g, i) => (
          <div key={i} className="border border-slate-100 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: '#6BA82E' }}>{g.section}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {g.items.filter(it => it.field_type !== undefined).map(f => (
                <div key={f.key}>
                  <Label className="text-xs text-slate-500">{f.label}</Label>
                  {f.field_type === 'checkbox' ? (
                    <label className="flex items-center gap-2 mt-1.5">
                      <input type="checkbox" checked={getVal(f) === 'true'} disabled={isLocked}
                             onChange={(e) => uField(f.key, e.target.checked ? 'true' : 'false')} />
                      <span className="text-xs">{getVal(f) === 'true' ? 'Yes' : 'No'}</span>
                    </label>
                  ) : f.field_type === 'textarea' ? (
                    <textarea value={getVal(f)} disabled={isLocked} onChange={(e) => uField(f.key, e.target.value)}
                      className="mt-1 w-full text-sm border border-slate-200 rounded-lg p-2" rows={2} />
                  ) : f.field_type === 'money' ? (
                    <MoneyInput value={getVal(f)} disabled={isLocked} onChange={(n) => uField(f.key, n == null ? '' : String(n))} className="mt-1" />
                  ) : f.field_type === 'number' ? (
                    <NumberInput value={getVal(f)} disabled={isLocked} onChange={(n) => uField(f.key, n == null ? '' : String(n))} className="mt-1" />
                  ) : f.field_type === 'date' ? (
                    <Input type="date" value={getVal(f)} disabled={isLocked} onChange={(e) => uField(f.key, e.target.value)} className="mt-1" />
                  ) : (
                    <Input type="text" value={getVal(f)} disabled={isLocked} onChange={(e) => uField(f.key, e.target.value)} className="mt-1" />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Signatories */}
      <div className="border border-slate-100 rounded-lg p-3">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: '#6BA82E' }}>Signatures</div>
        <div className="space-y-2">
          {c.signatories.map(s => (
            <div key={s.signatory_id} className={`rounded-lg p-3 border ${s.signed_at ? 'bg-emerald-50 border-emerald-100' : 'bg-slate-50 border-slate-100'}`} data-testid={`signatory-${s.signatory_id}`}>
              <div className="flex justify-between items-start flex-wrap gap-2">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest" style={{ color: '#4c7f21' }}>{s.role.replace(/_/g, ' ')}</div>
                  <div className="font-semibold text-sm" style={{ color: '#070B5F' }}>{s.name}</div>
                  {s.email && <div className="text-xs text-slate-500">{s.email}</div>}
                </div>
                <div className="text-right">
                  {s.signed_at ? (
                    <>
                      <div className="font-serif italic text-lg" style={{ color: '#6BA82E' }}>{s.signed_typed_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">
                        Signed {fromApi(s.signed_at).toLocaleString(undefined, { timeZoneName: 'short' })} · IP {s.ip_address} · Hash {s.signature_hash?.substring(0, 12)}…
                      </div>
                    </>
                  ) : (
                    <button onClick={() => setSigning(s.signatory_id)} disabled={isLocked} data-testid={`sign-btn-${s.signatory_id}`}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: '#070B5F' }}>
                      <FileSignature size={12} /> Sign
                    </button>
                  )}
                </div>
              </div>
              {signing === s.signatory_id && (
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                  <div>
                    <Label className="text-xs">Type your full legal name to sign</Label>
                    <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} className="mt-1" placeholder={s.name} data-testid={`typed-name-${s.signatory_id}`} />
                  </div>
                  <label className="flex items-start gap-2 text-xs text-slate-600">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid={`consent-${s.signatory_id}`} />
                    <span>I consent to sign this document electronically. My typed name, IP address, and timestamp will be recorded as tamper-evident audit data pursuant to the ESIGN Act (15 U.S.C. §7001) and Texas UETA.</span>
                  </label>
                  <div className="flex gap-2">
                    <button onClick={() => setSigning(null)} className="text-xs px-3 py-1.5 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
                    <button onClick={() => sign(s.signatory_id)} className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white" style={{ background: '#6BA82E' }} data-testid={`confirm-sign-${s.signatory_id}`}>Confirm Signature</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        {!isLocked && <AddSignatoryRow contractId={c.contract_id} onAdd={addSignatory} />}
      </div>
    </div>
  );
}

// Add a signer after the contract exists: pick a contact or type the name; email is optional.
function AddSignatoryRow({ contractId, onAdd }) {
  const [sig, setSig] = useState({ role: 'buyer', name: '', email: '' });
  const [busy, setBusy] = useState(false);
  const pick = (c) => { const { name, email } = contactFields(c); setSig((x) => ({ ...x, name, email })); };
  const add = async () => {
    if (busy) return;
    if (!sig.name.trim()) { toast({ title: 'Give the signatory a name' }); return; }
    setBusy(true);
    const ok = await onAdd({ role: sig.role, name: sig.name.trim(), email: sig.email.trim() || null });
    setBusy(false);
    if (ok) setSig((x) => ({ ...x, name: '', email: '' }));
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end mt-3 pt-3 border-t border-slate-100" data-testid={`add-signatory-${contractId}`}>
      <Select value={sig.role} onValueChange={(v) => setSig((x) => ({ ...x, role: v }))}>
        <SelectTrigger data-testid={`add-signatory-role-${contractId}`}><SelectValue /></SelectTrigger>
        <SelectContent>{SIGNER_ROLES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <ContactSearchInput placeholder="Full name (type to find a contact)" value={sig.name}
        onChange={(v) => setSig((x) => ({ ...x, name: v }))} onPick={pick} testid={`add-signatory-name-${contractId}`} className={FIELD_CLASS} />
      <Input placeholder="Email" value={sig.email} onChange={(e) => setSig((x) => ({ ...x, email: e.target.value }))} data-testid={`add-signatory-email-${contractId}`} />
      <button onClick={add} disabled={busy} className="text-xs font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-60" style={{ background: '#070B5F' }} data-testid={`add-signatory-btn-${contractId}`}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add signatory
      </button>
    </div>
  );
}

const SIGNER_ROLES = [['buyer', 'Buyer'], ['seller', 'Seller'], ['buyer_agent', 'Buyer Agent'], ['listing_agent', 'Listing Agent'], ['landlord', 'Landlord'], ['tenant', 'Tenant']];
const NO_DEAL = 'none';
function NewContractModal({ templates, onClose, onCreated }) {
  const [f, setF] = useState({ template_key: '20_15', signatories: [{ role: 'buyer', name: '', email: '' }] });
  const addSig = () => setF(x => ({ ...x, signatories: [...x.signatories, { role: 'seller', name: '', email: '' }] }));
  const updSig = (i, k, v) => setF(x => ({ ...x, signatories: x.signatories.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const remSig = (i) => setF(x => ({ ...x, signatories: x.signatories.filter((_, idx) => idx !== i) }));
  // Ticket 188: a contact pick fills the row's name + email; both stay editable.
  // A contact with no email blanks it rather than keep the last person's address.
  const pickSig = (i, c) => {
    const { name, email } = contactFields(c);
    setF(x => ({ ...x, signatories: x.signatories.map((s, idx) => idx === i ? { ...s, name, email } : s) }));
  };
  // Ticket 206: an optional deal. Its price, deposit, option and closing date fill the form.
  const [txs, setTxs] = useState([]);
  const [txId, setTxId] = useState(NO_DEAL);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { api.get('/re/transactions').then((r) => setTxs(r.data || [])).catch(() => setTxs([])); }, []);
  const submit = async () => {
    if (saving) return;
    const signatories = f.signatories.filter((s) => s.name.trim() || s.email.trim());
    if (signatories.some((s) => !s.name.trim())) { setError('Give every signatory a name.'); return; }
    const tx = txs.find((t) => t.transaction_id === txId);
    setError(''); setSaving(true);
    try {
      await api.post('/re/contracts', {
        ...f,
        signatories: signatories.map((s) => ({ ...s, name: s.name.trim(), email: s.email.trim() || null })),
        ...(tx ? { transaction_id: tx.transaction_id, property_id: tx.property_id || undefined } : {}),
      });
      onCreated();
    } catch (e) {
      setError(errorMessage(e));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="font-serif text-lg font-bold mb-4" style={{ color: '#070B5F' }}>New TREC Contract</div>
        <div className="space-y-3">
          <div><Label className="text-xs">TREC Template</Label>
            <Select value={f.template_key} onValueChange={(v) => setF(x => ({ ...x, template_key: v }))}>
              <SelectTrigger className="mt-1" data-testid="template-select"><SelectValue /></SelectTrigger>
              <SelectContent>{templates.map(t => <SelectItem key={t.template_key} value={t.template_key}>{t.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Deal (optional)</Label>
            <Select value={txId} onValueChange={setTxId}>
              <SelectTrigger className="mt-1" data-testid="contract-deal-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_DEAL}>No deal: start blank</SelectItem>
                {txs.map((t) => <SelectItem key={t.transaction_id} value={t.transaction_id}>{dealLabel(t)}</SelectItem>)}
              </SelectContent>
            </Select>
            {txId !== NO_DEAL && <div className="text-xs text-slate-500 mt-1">Price, earnest money, option and closing date are filled from this deal.</div>}
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Signatories</Label>
              <button onClick={addSig} className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"><Plus size={11} /> Add</button>
            </div>
            {f.signatories.map((s, i) => (
              <div key={i} className="grid grid-cols-3 gap-2 items-end mb-2">
                <Select value={s.role} onValueChange={(v) => updSig(i, 'role', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{SIGNER_ROLES.map(([k,l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <ContactSearchInput placeholder="Full name (type to find a contact)" value={s.name}
                  onChange={(v) => updSig(i, 'name', v)} onPick={(c) => pickSig(i, c)} testid={`signer-name-${i}`}
                  className={FIELD_CLASS} />
                <div className="flex gap-1">
                  <Input placeholder="Email" value={s.email} onChange={(e) => updSig(i, 'email', e.target.value)} data-testid={`signer-email-${i}`} />
                  <button onClick={() => remSig(i)} className="text-red-500 hover:bg-red-50 rounded px-2">×</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        {error && <div className="text-xs text-red-600 mt-3" role="alert" data-testid="new-contract-error">{error}</div>}
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving} className="text-sm px-4 py-2 rounded-lg text-white disabled:opacity-60" style={{ background: '#6BA82E' }} data-testid="submit-new-contract">{saving ? 'Creating…' : 'Create Contract'}</button>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// Helpers
// =========================================================================
function EmptyBox({ msg }) {
  return <div className="bg-white rounded-2xl p-8 border border-dashed border-slate-200 text-center text-sm text-slate-400">{msg}</div>;
}
